import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import useSEO from "../seo/use-seo";
import StandardPageHelmet from "../seo/components/StandardPageHelmet";
import {
  ChevronLeft,
  ChevronRight,
  Check,
} from "lucide-react";
import chooseUserTypeStyle from "./styles/chooseUserTypeStyle";
import complatedProfileStyle from "./styles/complateProfileStyle";
import {
  validateUserFirstName,
  validateUserLastName,
  validateBirthdayDate,
} from "../validators/profileFormValidators";
import ViewerLogo from "../assets/images/icons/viewerFlowImage.png";
import CreatorLogo from "../assets/images/icons/creatorFlowImage.png";
import Logo from "../assets/Logo.svg";
import ArrowBackRightIcon from "../assets/images/icons/arrowBackRight.png";
import {
  editProfileDataAPI,
  checkUsernameAPI,
} from "../api/userApi";
import { showToast } from "../utils/toast";
import { Redirect, useHistory } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../redux/typedHooks";
import { setUser, logoutUser } from "../redux/modules/userSlice";
import { clearTokens } from "../utils/tokens";
import { CITIES } from "../config/cities";
import { autoDetectCountry } from "../utils/countryDetection";
import {
  createDiditSessionAPI,
  deleteVerificationAPI,
  getDiditVerificationByUserIdAPI,
} from "../api/diditApi";
import UploadAvatarContent from "./components/uploadAvatarContent";
import UploadMediaModal from "./components/UploadMediaModal";
import useMedia from "../hooks/useMedia";
import OnboardingLayout from "../layouts/onboarding.layout";
import {
  trackOnboardingStarted,
  trackOnboardingCompleted,
  trackProfileCompleted,
  trackVerificationCompleted,
  trackVerificationStarted,
  trackVerificationStepCompleted,
  trackVerificationStepStarted,
  trackDiditVerificationFailureOutcome,
  registerDiditVerificationAttempt,
  normalizeDiditStatus,
  isDiditFailureStatus,
  isDiditSuccessStatus,
  getDiditVerificationAttemptCount,
} from "../analytics/onboardingMixpanel";
import {
  identifyMixpanelUser,
  resetMixpanelIdentity,
  setMixpanelPeopleProfile,
} from "../config/mixpanel";
import {
  getCreatorOnboardingFinalizeFields,
  isStagingCreatorOnboardingBypassEnabled,
  STAGING_MOCK_DIDIT_VERIFICATION_DATA,
  STAGING_MOCK_VERIFICATION_STATUS,
} from "../config/stagingCreatorOnboarding";
import { ProfileData, FormErrors } from "./types";
import ViewerProfileStep from "./components/ViewerProfileStep";
import CreatorProfileStep from "./components/CreatorProfileStep";
import CreatorVerificationStep from "./components/CreatorVerificationStep";

const stagingCreatorBypass = isStagingCreatorOnboardingBypassEnabled();

const ChooseUserType = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState(() => {
    // Get saved step from localStorage, default to 1
    const savedStep = localStorage.getItem("onboarding-step");
    return savedStep ? parseInt(savedStep, 10) : 1;
  });
  const history = useHistory();
  const classes = chooseUserTypeStyle();
  const seo = useSEO("choose-user-type");

  const user = useAppSelector<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    DOB: string;
    bio: string;
    isAdmin: boolean;
    profileImg: string;
    userType: {
      _id: string;
      title: string;
    };
  } | null>(state => state.user.currentUser);
  const dispatch = useAppDispatch();
  const onboardingFlowEnteredTracked = useRef(false);
  const profileCompletedTracked = useRef(false);
  const verificationStartedTracked = useRef(false);
  const verificationCompletedTracked = useRef(false);
  const lastPolledVerificationStatus = useRef<string | null>(null);

  const [selectedType, setSelectedType] = useState<string>(() => {
    // Get saved type from localStorage
    const savedType = localStorage.getItem("onboarding-selected-type");
    if (savedType) {
      return savedType;
    }
    // Default to "model" - will be updated by useEffect if user has different type
    return "model";
  });
  const creatorStyleProps = { selected: selectedType === "model" };
  const viewerStyleProps = { selected: selectedType === "viewer" };
  const creatorClasses = chooseUserTypeStyle(creatorStyleProps);
  const viewerClasses = chooseUserTypeStyle(viewerStyleProps);
  const profileStyle = complatedProfileStyle();
  const viewerIdType = `6800a68085ec077c7c266c27`;
  const modelIdType = `6800a63485ec077c7c266c25`;

  const [profileData, setProfileData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    DOB: string;
    usertype: string;
    publicImageUsageConsent: boolean;
    gender: string;
    bio: string;
    location: string;
    sexualOrientation: string;
    ethnicity: string;
    annualIncome: string;
    country?: string;
    city?: string;
    subdivision?: string;
    profileImg: File | null;
  }>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    username:
      user?.username && user.username.startsWith("user_")
        ? ""
        : user?.username || "",
    DOB: user?.DOB || "", // Default to empty string instead of user?.DOB
    usertype: user?.userType?._id || modelIdType,
    publicImageUsageConsent: false,
    gender: "",
    bio: user?.bio || "",
    location: "",
    sexualOrientation: "",
    ethnicity: "",
    annualIncome: "",
    country: "",
    city: "",
    subdivision: "",
    profileImg: null,
  });

  const { imageUrl: profileImgUrl } = useMedia(user?.profileImg);

  const [, setProfileCompleted] = useState(false);
  const [, setProfileViewerCompleted] = useState(false);
  const [diditVerificationData, setDiditVerificationData] = useState<any>(null);
  const [verificationStatus, setVerificationStatus] =
    useState<string>("pending");
  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);
  const [sessionUrl, setSessionUrl] = useState<string | undefined>(() => {
    // Get saved session URL from sessionStorage
    return sessionStorage.getItem("didit-session-url") || undefined;
  });
  // iframe visibility is derived from sessionUrl

  // Error states for viewer form
  const [viewerErrors, setViewerErrors] = useState<{
    firstName?: string;
    lastName?: string;
    username?: string;
    DOB?: string;
    profileImg?: string;
    bio?: string;
  }>({});

  // Username availability check state
  const [isCheckingUsername, setIsCheckingUsername] = useState<boolean>(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
    null
  );

  const [creatorErrors, setCreatorErrors] = useState<{
    firstName?: string;
    lastName?: string;
    username?: string;
    DOB?: string;
    profileImg?: string;
    bio?: string;
  }>({});
  const [isGoToPayment, setIsGoToPayment] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState<boolean>(false);
  const [isSubmittingProfile, setIsSubmittingProfile] = useState<boolean>(false);
  // Agreement checkboxes state
  const [agreementAccepted, setAgreementAccepted] = useState<boolean>(false);
  const [imageUsageAgreed, setImageUsageAgreed] = useState<boolean>(false);
  // Upload media modal state
  const [uploadMediaModalOpen, setUploadMediaModalOpen] =
    useState<boolean>(false);

  const isAdult = () => {
    if (!profileData.DOB || profileData.DOB.trim() === "") return false;
    const birthDate = new Date(profileData.DOB);
    if (Number.isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 18;
  };
  const validateAllFields = () => {
    const isFirstNameValid =
      validateUserFirstName(profileData.firstName) === true;
    const isLastNameValid = validateUserLastName(profileData.lastName) === true;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email);
    const isDOBValid = validateBirthdayDate(profileData.DOB) === true;
    // const isBioValid = validateUserBio(profileData.bio) === true;
    // const isGenderValid = validateUserGender(profileData.gender) === true;
    // const isLocationValid = validateUserLocation(profileData.location) === true;
    // const isCityValid = profileData.city && profileData.city.length >= 2;
    // const isSexualOrientationValid =
    //   validateUserSexualOrientation(profileData.sexualOrientation) === true;
    // const isEthnicityValid =
    //   validateUserEthnicity(profileData.ethnicity) === true;

    return (
      isFirstNameValid && isLastNameValid && isEmailValid && isDOBValid
      // isGenderValid &&
      // isBioValid &&
      // isLocationValid &&
      // isCityValid &&
      // isSexualOrientationValid &&
      // isEthnicityValid
    );
  };
  // Validation functions for creator form
  const validateCreatorField = (
    field: keyof typeof creatorErrors,
    value: any
  ) => {
    let error = "";

    switch (field) {
      case "firstName":
        if (!value || value.trim() === "") {
          error = "First name is required";
        } else if (value.length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
      case "lastName":
        if (!value || value.trim() === "") {
          error = "Last name is required";
        } else if (value.length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
      case "username":
        if (value && value.trim() !== "") {
          const usernameRegex = /^[a-zA-Z0-9_-]+$/;
          if (value.length < 3) {
            error = "Username must be at least 3 characters";
          } else if (value.length > 16) {
            error = "Username must be less than 16 characters";
          } else if (!usernameRegex.test(value)) {
            error =
              "Username can only contain letters, numbers, underscores, and hyphens";
          }
        }
        break;
      case "DOB":
        if (!value || value.trim() === "") {
          error = "Date of birth is required";
        } else {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 18) {
            error = "You must be at least 18 years old";
          }
        }
        break;
      case "profileImg":
        if (!value) {
          error = "Profile image is required";
        }
        break;
      case "bio":
        // Bio is optional, no validation needed
        break;
    }

    return error;
  };

  const handleCreatorFieldChange = (
    field: any,
    value: any
  ) => {
    // Update profile data
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (creatorErrors[field as keyof typeof creatorErrors]) {
      setCreatorErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }

    // Validate next state and log errors to track
    // const nextProfileData = {
    //   ...profileData,
    //   [field]: value,
    // };
    // const errors = {
    //   firstName: validateCreatorField("firstName", nextProfileData.firstName),
    //   lastName: validateCreatorField("lastName", nextProfileData.lastName),
    //   username: validateCreatorField("username", nextProfileData.username),
    //   DOB: validateCreatorField("DOB", nextProfileData.DOB),
    //   profileImg: validateCreatorField("profileImg", nextProfileData.profileImg || profileImgUrl),
    // };
    // const isDOBAdult = () => {
    //   if (!nextProfileData.DOB || nextProfileData.DOB.trim() === "") return false;
    //   const birthDate = new Date(nextProfileData.DOB);
    //   if (Number.isNaN(birthDate.getTime())) return false;
    //   const today = new Date();
    //   let age = today.getFullYear() - birthDate.getFullYear();
    //   const monthDiff = today.getMonth() - birthDate.getMonth();
    //   if (
    //     monthDiff < 0 ||
    //     (monthDiff === 0 && today.getDate() < birthDate.getDate())
    //   ) {
    //     age--;
    //   }
    //   return age >= 18;
    // };
    // const isFormValid =
    //   nextProfileData.firstName.trim() !== "" &&
    //   nextProfileData.lastName.trim() !== "" &&
    //   nextProfileData.DOB.trim() !== "" &&
    //   isDOBAdult() &&
    //   (nextProfileData.profileImg !== null || profileImgUrl !== null) &&
    //   usernameAvailable !== false;

    // console.log("Creator Form Validation Status:", {
    //   fieldChanged: field,
    //   newValue: value,
    //   errors,
    //   isFormValid,
    // });
  };

  const handleCreatorFieldBlur = (
    field: any,
    value: any
  ) => {
    const error = validateCreatorField(field as keyof typeof creatorErrors, value);
    setCreatorErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const isCreatorFormValid = () => {
    const isDOBAdult = isAdult();
    return (
      profileData.firstName.trim() !== "" &&
      profileData.lastName.trim() !== "" &&
      profileData.DOB.trim() !== "" &&
      isDOBAdult &&
      (profileData.profileImg !== null || profileImgUrl !== null) &&
      usernameAvailable !== false
    );
  };

  // Validation functions for viewer form
  const validateViewerField = (
    field: keyof typeof viewerErrors,
    value: any
  ) => {
    let error = "";

    switch (field) {
      case "firstName":
        if (!value || value.trim() === "") {
          error = "First name is required";
        } else if (value.length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
      case "lastName":
        if (!value || value.trim() === "") {
          error = "Last name is required";
        } else if (value.length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
      case "username":
        if (!value || value.trim() === "") {
          error = "Username is required";
        } else if (value.length < 3) {
          error = "Username must be at least 3 characters";
        }
        break;
      case "DOB":
        if (!value || value.trim() === "") {
          error = "Date of birth is required";
        } else {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 18) {
            error = "You must be at least 18 years old";
          }
        }
        break;
      case "profileImg":
        if (!value) {
          error = "Profile image is required";
        }
        break;
      case "bio":
        // Bio is optional, no validation needed
        break;
    }

    return error;
  };

  const handleViewerFieldChange = (
    field: any,
    value: any
  ) => {
    // Update profile data
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing, except for username "already taken" error
    if (viewerErrors[field as keyof typeof viewerErrors]) {
      // Don't clear "already taken" error automatically - let the API check handle it
      if (
        field === "username" &&
        viewerErrors[field as keyof typeof viewerErrors] === "This username is already taken"
      ) {
        // Keep the error, but reset availability to trigger new check
        setUsernameAvailable(null);
      } else {
        setViewerErrors(prev => ({
          ...prev,
          [field]: "",
        }));
      }
    }

    // Reset username availability when username changes
    if (field === "username") {
      setUsernameAvailable(null);
    }

    // Validate next state and log errors to track
    const nextProfileData = {
      ...profileData,
      [field]: value,
    };
    const errors = {
      firstName: validateViewerField("firstName", nextProfileData.firstName),
      lastName: validateViewerField("lastName", nextProfileData.lastName),
      username: validateViewerField("username", nextProfileData.username),
      DOB: validateViewerField("DOB", nextProfileData.DOB),
      profileImg: validateViewerField("profileImg", nextProfileData.profileImg || profileImgUrl),
    };
    const isDOBAdult = () => {
      if (!nextProfileData.DOB || nextProfileData.DOB.trim() === "") return false;
      const birthDate = new Date(nextProfileData.DOB);
      if (Number.isNaN(birthDate.getTime())) return false;
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age >= 18;
    };
    const isFormValid =
      nextProfileData.firstName.trim() !== "" &&
      nextProfileData.lastName.trim() !== "" &&
      nextProfileData.email.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextProfileData.email) &&
      nextProfileData.DOB.trim() !== "" &&
      isDOBAdult() &&
      (nextProfileData.profileImg !== null || profileImgUrl !== null) &&
      nextProfileData.username.trim() !== "" &&
      (field === "username" ? null : usernameAvailable) !== false;

    // console.log("Viewer Form Validation Status:", {
    //   fieldChanged: field,
    //   newValue: value,
    //   errors,
    //   isFormValid,
    // });
  };

  const handleViewerFieldBlur = (
    field: any,
    value: any
  ) => {
    const error = validateViewerField(field as keyof typeof viewerErrors, value);
    setViewerErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  // Username availability check with debounce
  useEffect(() => {
    const username = profileData.username?.trim();

    // Don't check if username is empty or too short
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      setIsCheckingUsername(false);
      return;
    }

    // Don't check if username format is invalid
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setUsernameAvailable(null);
      setIsCheckingUsername(false);
      return;
    }

    // Set checking state
    setIsCheckingUsername(true);

    // Debounce: wait 430ms before checking
    const timeoutId = setTimeout(async () => {
      try {
        const response = await checkUsernameAPI(username);
        if (response.success) {
          // If username exists, it's not available
          const isAvailable = !response.data.exists;
          setUsernameAvailable(isAvailable);

          if (response.data.exists) {
            // Always show error if username is taken
            setViewerErrors(prev => ({
              ...prev,
              username: "This username is already taken",
            }));
          } else {
            // Clear "already taken" error only if username is available
            setViewerErrors(prev => {
              if (prev.username === "This username is already taken") {
                return {
                  ...prev,
                  username: "",
                };
              }
              return prev;
            });
          }
        }
      } catch (error: any) {
        console.error("Error checking username:", error);
        // On error, check if it's because username is taken
        if (
          error?.data?.exists === true ||
          error?.message?.toLowerCase().includes("taken")
        ) {
          setUsernameAvailable(false);
          setViewerErrors(prev => ({
            ...prev,
            username: "This username is already taken",
          }));
        } else {
          // On other errors, don't set availability (keep null)
          setUsernameAvailable(null);
        }
      } finally {
        setIsCheckingUsername(false);
      }
    }, 430);

    // Cleanup timeout on unmount or when username changes
    return () => {
      clearTimeout(timeoutId);
    };
  }, [profileData.username]);

  const isViewerFormValid = () => {
    const isDOBAdult = isAdult();
    return (
      profileData.firstName.trim() !== "" &&
      profileData.lastName.trim() !== "" &&
      profileData.email.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email) &&
      profileData.DOB.trim() !== "" &&
      isDOBAdult &&
      (profileData.profileImg !== null || profileImgUrl !== null) &&
      profileData.username.trim() !== "" &&
      usernameAvailable !== false // Username must be available
    );
  };

  const validateAllFieldsViewer = () => {
    const isFirstNameValid =
      validateUserFirstName(profileData.firstName) === true;
    const isLastNameValid = validateUserLastName(profileData.lastName) === true;
    // const isEmailValid = validateUserEmail(profileData.email) === true;
    const isDOBValid = validateBirthdayDate(profileData.DOB) === true;
    // const isBioValid = validateUserBio(profileData.bio) === true;
    // const isGenderValid = validateUserGender(profileData.gender) === true;
    // const isLocationValid = validateUserLocation(profileData.location) === true;
    // const isCityValid = profileData.city && profileData.city.length >= 2;
    // const isSexualOrientationValid =
    //   validateUserSexualOrientation(profileData.sexualOrientation) === true;
    // const isEthnicityValid =
    //   validateUserEthnicity(profileData.ethnicity) === true;
    // const isAnnualIncomeValid =
    //   validateUserAnnualIncome(profileData.annualIncome) === true;
    return (
      isFirstNameValid &&
      isLastNameValid &&
      // isEmailValid &&
      isDOBValid
      // isGenderValid &&
      // isBioValid &&
      // isLocationValid &&
      // isCityValid &&
      // isSexualOrientationValid &&
      // isEthnicityValid &&
      // isAnnualIncomeValid
    );
  };

  // Function to save current step to localStorage
  const saveCurrentStep = useCallback((stepNumber: number) => {
    localStorage.setItem("onboarding-step", stepNumber.toString());
  }, []);

  // Function to save selected type to localStorage
  const saveSelectedType = useCallback((type: string) => {
    localStorage.setItem("onboarding-selected-type", type);
  }, []);

  // Function to save session URL to sessionStorage
  const saveSessionUrl = useCallback((url: string) => {
    sessionStorage.setItem("didit-session-url", url);
  }, []);

  // Function to clear session URL from sessionStorage
  const clearSessionUrl = useCallback(() => {
    sessionStorage.removeItem("didit-session-url");
  }, []);

  // Function to clear saved step (when onboarding is complete)
  const clearSavedStep = useCallback(() => {
    localStorage.removeItem("onboarding-step");
    localStorage.removeItem("onboarding-selected-type");
    clearSessionUrl(); // Clear session URL as well
  }, [clearSessionUrl]);

  // Initialize selectedType from user only if not already saved in localStorage
  useEffect(() => {
    if (user) {
      const savedType = localStorage.getItem("onboarding-selected-type");
      if (!savedType) {
        // Only set from user if no saved type exists
        const userType = user?.userType?.title || "model";
        setSelectedType(userType);
        saveSelectedType(userType);
      }
    }
  }, [user, saveSelectedType]);

  // Clear default "user_XXXXX" username on onboarding
  useEffect(() => {
    if (user?.username && user.username.startsWith("user_")) {
      setProfileData(prev => ({
        ...prev,
        username: "",
      }));
    }
  }, [user?.username]);

  // Save current step whenever it changes
  useEffect(() => {
    saveCurrentStep(step);
  }, [step, saveCurrentStep]);

  useEffect(() => {
    if (user?.id) {
      identifyMixpanelUser(user.id);
      setMixpanelPeopleProfile({
        email: user?.email,
        name: user?.username || `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      });
    }
  }, [user?.id, user?.email, user?.username, user?.firstName, user?.lastName]);

  useEffect(() => {
    if (onboardingFlowEnteredTracked.current) {
      return;
    }
    onboardingFlowEnteredTracked.current = true;
    trackOnboardingStarted({
      account_type: selectedType,
      user_id: user?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- один раз при вході на onboarding
  }, []);

  useEffect(() => {
    if (step >= 2 && !profileCompletedTracked.current) {
      profileCompletedTracked.current = true;
      trackProfileCompleted({
        account_type: selectedType,
        user_id: user?.id,
      });
    }
  }, [step, selectedType, user?.id]);

  // Save selected type whenever it changes
  useEffect(() => {
    saveSelectedType(selectedType);
  }, [selectedType, saveSelectedType]);

  // Save session URL whenever it changes
  useEffect(() => {
    if (sessionUrl) {
      saveSessionUrl(sessionUrl);
    }
  }, [sessionUrl, saveSessionUrl]);

  // sessionUrl changes are handled by UI
  useEffect(() => {}, [sessionUrl]);

  useEffect(() => {
    setProfileCompleted(!!validateAllFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileData.firstName,
    profileData.lastName,
    profileData.DOB,
    profileData.gender,
    profileData.bio,
    profileData.location,
    profileData.city,
    profileData.sexualOrientation,
    profileData.ethnicity,
    profileData.profileImg,
    agreementAccepted,
  ]);

  useEffect(() => {
    setProfileViewerCompleted(!!validateAllFieldsViewer());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileData.firstName,
    profileData.lastName,
    profileData.DOB,
    profileData.gender,
    profileData.bio,
    profileData.location,
    profileData.city,
    profileData.sexualOrientation,
    profileData.ethnicity,
    profileData.annualIncome,
    profileData.profileImg,
  ]);

  const handleProfileChange = () => {
    // const validationResult = validateAllFields({
    //     ...profileData,
    //     publicImageUsageConsent:
    //         selectedType === "model" ? agreementAccepted : true,
    // });
    // if (validationResult !== true) {
    //     showToast(validationResult, "error");
    //     return;
    // }
    const isFirstNameValid = validateUserFirstName(profileData.firstName) === true;
    const isLastNameValid = validateUserLastName(profileData.lastName) === true;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email);
    const isDOBValid = validateBirthdayDate(profileData.DOB) === true;

    const isValid = isFirstNameValid && isLastNameValid && isEmailValid && isDOBValid;

    if (!isValid) {
      console.error("Form validation failed during submit:", {
        firstName: { value: profileData.firstName, isValid: isFirstNameValid },
        lastName: { value: profileData.lastName, isValid: isLastNameValid },
        email: { value: profileData.email, isValid: isEmailValid },
        DOB: { value: profileData.DOB, isValid: isDOBValid },
      });
      showToast("Please complete all required fields", "error");
      return;
    }
    setIsSubmittingProfile(true);
    const formData = new FormData();
    formData.append("firstName", profileData.firstName);
    formData.append("lastName", profileData.lastName);
    formData.append("email", profileData.email);
    // formData.append("email", profileData.email);
    formData.append("DOB", profileData.DOB);
    formData.append("usertype", profileData.usertype);
    // formData.append("bio", profileData.bio);
    // formData.append("gender", profileData.gender);
    // formData.append("location", profileData.location);
    // formData.append("sexualOrientation", profileData.sexualOrientation);
    // formData.append("ethnicity", profileData.ethnicity);
    // formData.append("annualIncome", profileData.annualIncome);
    // formData.append(
    //   "publicImageUsageConsent",
    //   (selectedType === "model" ? agreementAccepted : true).toString()
    // );
    if (profileData.country) {
      formData.append("country", profileData.country);
    }
    if (profileData.city) {
      formData.append("city", profileData.city);
    }
    if (profileData.subdivision) {
      formData.append("subdivision", profileData.subdivision);
    }
    // Build location string from city, subdivision, and country
    if (profileData.city || profileData.country) {
      const locationParts = [];
      if (profileData.city) locationParts.push(profileData.city);
      if (profileData.subdivision) locationParts.push(profileData.subdivision);
      if (profileData.country) locationParts.push(profileData.country);
      formData.append("location", locationParts.join(", "));
    }
    if (profileData.profileImg instanceof File) {
      formData.append("image", profileData.profileImg);
    }
    editProfileDataAPI(formData)
      .then(res => {
        if (res?.success) {
          // Update Redux store with fresh user data
          if (res?.user && user) {
            dispatch(
              setUser({
                ...user,
                ...res?.user,
              })
            );
          }

          showToast("Profile updated successfully!");
          if (selectedType === "model") {
            setIsSubmittingProfile(false);
            return nextStep();
            // createDiditSessionAPI(
            //   user?.id,
            //   `${window.location.origin}/verification-success`
            // )
            //   .then(() => {
            //     nextStep(); // Go to Didit verification status step
            //   })
            //   .catch(err => {
            //     showToast("Error starting verification process", "error");
            //     console.error("Error starting Didit verification:", err);
            //   });
          } else {
            // Viewer: complete onboarding (no follow step)
            editProfileDataAPI({
              isOnboarding: true,
              isOnboaring: true,
              validated: true,
            })
              .then((finishRes: any) => {
                if (finishRes?.user && user) {
                  dispatch(
                    setUser({
                      ...user,
                      ...finishRes.user,
                    })
                  );
                }
                showToast("Onboarding completed!", "success");
                clearSavedStep();
                trackOnboardingCompleted({
                  account_type: "viewer",
                  user_id: user?.id,
                });
                setIsSubmittingProfile(false);
                history.push("/");
              })
              .catch((e: any) => {
                console.error("Failed to complete onboarding API call:", e);
                showToast(
                  e?.message || "Failed to complete onboarding",
                  "error"
                );
                setIsSubmittingProfile(false);
              });
          }
        } else {
          console.error("editProfileDataAPI responded with success=false:", res);
          showToast("Update profile error!", "error");
          setIsSubmittingProfile(false);
        }
      })
      .catch(err => {
        console.error("editProfileDataAPI request failed:", err);
        showToast(err?.message || "Server error!", "error");
        setIsSubmittingProfile(false);
      });
  };

  const handleTypeChange = async (type: string, isNext = true) => {
    const updatedProfileData = {
      ...profileData,
      usertype: type === "model" ? modelIdType : viewerIdType,
    };

    setProfileData(updatedProfileData);
    setSelectedType(type);
    saveSelectedType(type); // Save selected type to localStorage
    editProfileDataAPI({ usertype: updatedProfileData.usertype })
      .then(res => {
        // Update Redux store with fresh user data
        if (res?.user && user) {
          dispatch(
            setUser({
              ...user,
              ...res?.user,
              userType: {
                _id: type === "model" ? modelIdType : viewerIdType,
                title: type,
                description: type === "model" ? "model" : "viewer",
              },
            })
          );
        }

        showToast(`Profile type changed to ${type}`);
        isNext && nextStep();
      })
      .catch(err => {
        showToast(`Error while changing type to ${type}`, "error");
        console.error("API error:", err);
      });
  };

  /* const startDiscover = () => {
    // For viewers, complete profile and redirect to home
    editProfileDataAPI({
      validated: true,
      isOnboarding: true,
    })
      .then(res => {
        if (res?.success) {
          // Update Redux store with fresh user data
          if (res?.user && user) {
            dispatch(
              setUser({
                ...user,
                ...res?.user,
              })
            );
          }

          clearSavedStep(); // Clear saved step when onboarding is complete
          showToast("Profile set up successfully!");
          history.push("/");
        } else {
          showToast("Error setting up profile data!", "error");
        }
      })
      .catch(err => {
        showToast(err?.message || "Server error!", "error");
        console.log("Update profile error: ", err);
      });
  }; */

  /* const finishOnboarding = () => {
    // Complete the onboarding process
    editProfileDataAPI({
      validated: true,
    })
      .then(res => {
        if (res?.success) {
          // Update Redux store with fresh user data
          if (res?.user && user) {
            dispatch(
              setUser({
                ...user,
                ...res?.user,
              })
            );
          }

          clearSavedStep(); // Clear saved step when onboarding is complete
          showToast("Onboarding completed successfully!");
          history.push("/");
        } else {
          showToast("Error completing onboarding!", "error");
        }
      })
      .catch(err => {
        showToast(err?.message || "Server error!", "error");
        console.log("Update profile error: ", err);
      });
  }; */

  const handleCompleteOnboarding = async () => {
    try {
      setIsFinalizing(true);
      const res: any = await editProfileDataAPI({
        isOnboarding: true,
        isOnboaring: true,
        validated: true,
        contentProviderAgreementAccepted: true,
        ...getCreatorOnboardingFinalizeFields(),
      });
      if (res?.user && user) {
        dispatch(
          setUser({
            ...user,
            ...res.user,
            ...(stagingCreatorBypass ? { ageProofed: true } : {}),
          })
        );
      }
      showToast("Onboarding completed!", "success");
      clearSavedStep();
      trackOnboardingCompleted({
        account_type: "model",
        user_id: user?.id,
      });
      history.push("/");
    } catch (e: any) {
      showToast(
        e?.message || "Failed to complete onboarding",
        "error"
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  //   DiditVerification
  const startDiditVerification = async () => {
    if (stagingCreatorBypass) {
      setDiditVerificationData(STAGING_MOCK_DIDIT_VERIFICATION_DATA);
      setVerificationStatus(STAGING_MOCK_VERIFICATION_STATUS);
      sessionStorage.setItem(
        "diditVerificationStatus",
        STAGING_MOCK_VERIFICATION_STATUS
      );
      clearSessionUrl();
      showToast("Staging: identity verification skipped.", "info");
      return;
    }

    if (!verificationStartedTracked.current) {
      verificationStartedTracked.current = true;
      trackVerificationStarted({ user_id: user?.id });
      trackVerificationStepStarted({
        user_id: user?.id,
        step_type: "id_check",
      });
    }

    try {
      if (
        diditVerificationData?.didit_verifications?.[0]?.status === "Approved"
      ) {
        showToast("Your verification is already approved!", "success");
      } else if (
        diditVerificationData?.didit_verifications?.[0]?.status ===
        "In Progress"
      ) {
        showToast("Your verification is already in progress.", "info");
      } else if (
        diditVerificationData?.didit_verifications?.[0]?.status === "In Review"
      ) {
        showToast(
          "Your verification is under review. Please wait for the result.",
          "info"
        );
      } else if (
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "Declined" ||
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "KYC Expired" ||
        diditVerificationData?.didit_verifications?.[0]?.status === "Expired" ||
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "Abandoned" ||
        diditVerificationData?.didit_verifications?.[0]?.status === "unknown" ||
        diditVerificationData?.didit_verifications?.[0]?.status === "pending" ||
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "Not Started"
      ) {
        // For these statuses, remove previous session and create new one
        showToast(
          `Your previous verification was ${diditVerificationData?.didit_verifications?.[0]?.status}. Starting a new verification session.`,
          "warning"
        );
        if (diditVerificationData?.current_didit_verification_id) {
          try {
            await deleteVerificationAPI(
              diditVerificationData?.current_didit_verification_id
            );
          } catch (error) {
            console.error(
              "Error deleting previous verification session:",
              error
            );
            showToast(
              "Warning: Could not delete previous session, but continuing with new verification",
              "warning"
            );
          }
        }
      }

      setPopupBlocked(false); // Reset popup blocked state
      setSessionUrl(undefined); // Reset iframe state
      clearSessionUrl(); // Clear any existing session URL

      const result = await createDiditSessionAPI(
        user?.id,
        `${window.location.origin}/verification-success`
      );

      // Store the session data and show iframe
      if (result?.session_url) {
        verificationCompletedTracked.current = false;
        registerDiditVerificationAttempt({
          user_id: user?.id,
          source: "user_click",
        });
        setSessionUrl(result.session_url);
        saveSessionUrl(result.session_url); // Save to sessionStorage
        // iframe visible due to sessionUrl

        showToast(
          "Verification process started. Please complete the verification below."
        );
      }
    } catch (error) {
      console.error("Error starting Didit verification:", error);

      // Handle specific error cases
      if ((error as any)?.type === "ACTIVE_VERIFICATION_EXISTS") {
        // User already has an active verification
        const existingStatus = (error as any).status;
        setVerificationStatus(existingStatus);

        if (existingStatus === "approved") {
          showToast("Your verification is already approved!", "success");
        } else if (existingStatus === "in_progress") {
          showToast("Your verification is already in progress.", "info");
        } else if (existingStatus === "in_review") {
          showToast(
            "Your verification is under review. Please wait for the result.",
            "info"
          );
        } else if (
          existingStatus === "declined" ||
          existingStatus === "kyc_expired" ||
          existingStatus === "expired" ||
          existingStatus === "abandoned" ||
          existingStatus === "unknown" ||
          existingStatus === "pending"
        ) {
          // For these statuses, remove previous session and create new one
          showToast(
            `Your previous verification was ${existingStatus}. Starting a new verification session.`,
            "warning"
          );
          await startNewVerificationSession();
        } else {
          showToast(`Verification status: ${existingStatus}`, "info");
        }
      } else {
        trackDiditVerificationFailureOutcome({
          user_id: user?.id,
          status: "session_create_error",
          failure_source: "session_create",
          failure_reason:
            (error as any)?.message ||
            (error instanceof Error ? error.message : "session_create_failed"),
          attempt_number: getDiditVerificationAttemptCount(),
        });
        showToast("Error starting verification process", "error");
      }
    }
  };

  const startNewVerificationSession = async () => {
    try {
      // Clear any existing session data first
      clearSessionUrl();
      setSessionUrl(undefined);
      // no-op

      // Try to create a new session (backend should handle removing previous session)
      const result = await createDiditSessionAPI(
        user?.id,
        `${window.location.origin}/verification-success`
      );

      if (result?.session_url) {
        verificationCompletedTracked.current = false;
        registerDiditVerificationAttempt({
          user_id: user?.id,
          source: "retry_after_failure",
        });
        setSessionUrl(result.session_url);
        saveSessionUrl(result.session_url);
        // no-op
        setVerificationStatus("in_progress");
        showToast("New verification session started successfully!");
      }
    } catch (error) {
      console.error("Error starting new verification session:", error);

      // Handle the case where there's still an active session
      if ((error as any)?.type === "ACTIVE_VERIFICATION_EXISTS") {
        const existingStatus = (error as any).status;

        if (
          existingStatus === "declined" ||
          existingStatus === "kyc_expired" ||
          existingStatus === "expired" ||
          existingStatus === "abandoned" ||
          existingStatus === "unknown" ||
          existingStatus === "pending"
        ) {
          showToast(
            `Unable to start new session. Previous ${existingStatus} session still exists. Please contact support.`,
            "error"
          );
        } else {
          showToast(`Previous verification status: ${existingStatus}`, "info");
        }
      } else {
        trackDiditVerificationFailureOutcome({
          user_id: user?.id,
          status: "session_create_error",
          failure_source: "api_error",
          failure_reason:
            (error as any)?.message ||
            (error instanceof Error ? error.message : "retry_session_failed"),
          attempt_number: getDiditVerificationAttemptCount(),
        });
        showToast("Error starting new verification session", "error");
      }
    }
  };

  const getStatusMessage = (status: string) => {
    const statusMessages = {
      "Not Started": {
        title: "",
        message: "",
        color: "#f06ec4",
        canRestart: true,
      },
      "In Progress": {
        title: "⏳ Verification in Progress",
        message:
          "Great! You've started the verification process. Please complete the steps below to finish your identity verification. This usually takes just a few minutes.",
        color: "#F59E0B",
        canRestart: false,
      },
      Approved: {
        title: "🎉 Verification Complete!",
        message:
          "Excellent! Your identity has been successfully verified. You're all set to start your creator journey. Click 'Next' to continue to the final step.",
        color: "#10B981",
        canRestart: false,
      },
      Declined: {
        title: "❌ Verification Needs Attention",
        message:
          "We weren't able to verify your identity with the information provided. This could be due to unclear documents or missing information. Don't worry - you can try again with better quality documents.",
        color: "#EF4444",
        canRestart: true,
      },
      "KYC Expired": {
        title: "⏰ Verification Expired",
        message:
          "Your previous verification has expired and needs to be renewed. This is a standard security measure. You can start a fresh verification process right now.",
        color: "#F59E0B",
        canRestart: true,
      },
      "In Review": {
        title: "🔍 Under Review",
        message:
          "Your verification is being reviewed by our compliance team. This is normal for some cases and usually takes 24-48 hours. We'll notify you via email once the review is complete.",
        color: "#3B82F6",
        canRestart: false,
      },
      Expired: {
        title: "⏰ Session Expired",
        message:
          "Your verification session timed out. This happens when the process takes too long. No worries - you can start a new verification session right away.",
        color: "#F59E0B",
        canRestart: true,
      },
      Abandoned: {
        title: "🚫 Verification Incomplete",
        message:
          "It looks like the verification process wasn't completed. This can happen if you close the browser or navigate away. You can start fresh whenever you're ready.",
        color: "#6B7280",
        canRestart: true,
      },
      unknown: {
        title: "❓ Status Unclear",
        message:
          "We're having trouble determining your verification status. This is rare, but easily fixed. You can start a new verification process to get back on track.",
        color: "#6B7280",
        canRestart: true,
      },
    };

    return (
      statusMessages[status as keyof typeof statusMessages] || {
        title: "❓ Unknown Status",
        message: `Verification status: ${status}. Please contact support if you need assistance.`,
        color: "#6B7280",
        canRestart: true,
      }
    );
  };

  /* const redirectToProfile = () => {
    // Redirect to profile with pending approval status
    history.push(`/${user?.username || user?.id}`);
  }; */

  const handleManualPopupOpen = () => {
    // Try to show the verification iframe manually
    if (sessionUrl) {
      // no-op
      setPopupBlocked(false);
      showToast("Verification iframe opened successfully!");
    } else if (diditVerificationData?.session_url) {
      setSessionUrl(diditVerificationData.session_url);
      saveSessionUrl(diditVerificationData.session_url);
      // no-op
      setPopupBlocked(false);
      showToast("Verification iframe opened successfully!");
    } else {
      startDiditVerification();
    }
  };

  const nextStep = useCallback(
    (currentStep?: number) => {
      const newStep = currentStep || step + 1;
      setStep(newStep);
      saveCurrentStep(newStep);
    },
    [step, saveCurrentStep]
  );
  const prevStep = useCallback(
    (currentStep?: number) => {
      const newStep = currentStep || step - 1;
      setStep(newStep);
      saveCurrentStep(newStep);
    },
    [step, saveCurrentStep]
  );
  const goToStep = useCallback(
    (targetStep: number) => {
      setStep(targetStep);
      saveCurrentStep(targetStep);
    },
    [saveCurrentStep]
  );
  // const fileInputRef = useRef<HTMLInputElement | null>(null);
  // const buildLocationString = (city?: string, country?: string) =>
  //   city && country ? `${city}, ${country}` : city || country || "";

  const parseLocationString = (loc?: string) => {
    if (!loc) return { city: "", country: "" };
    const [cityPart, ...rest] = loc.split(",").map(s => s.trim());
    return { city: cityPart || "", country: rest.join(", ").trim() || "" };
  };

  // Auto-detect country on mount
  useEffect(() => {
    const userCountry = (user as any)?.country;
    if (!profileData.country && !userCountry) {
      autoDetectCountry().then(detectedCountry => {
        if (detectedCountry) {
          setProfileData(prev => ({
            ...prev,
            country: detectedCountry,
          }));
        }
      });
    } else if (userCountry && !profileData.country) {
      setProfileData(prev => ({
        ...prev,
        country: userCountry,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profileData && !profileData.country && profileData.location) {
      const { city, country } = parseLocationString(profileData.location);
      if (city || country) {
        setProfileData(p => ({
          ...p,
          city: p.city || city,
          country: p.country || country,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData.location]);

  // Staging: mock approved identity so creator step 3 never opens Didit/Cloudflare.
  useEffect(() => {
    if (!stagingCreatorBypass || selectedType !== "model") {
      return;
    }
    setDiditVerificationData(STAGING_MOCK_DIDIT_VERIFICATION_DATA);
    setVerificationStatus(STAGING_MOCK_VERIFICATION_STATUS);
    sessionStorage.setItem(
      "diditVerificationStatus",
      STAGING_MOCK_VERIFICATION_STATUS
    );
    clearSessionUrl();
  }, [selectedType, clearSessionUrl]);

  // Poll Didit verification status every second for model users
  useEffect(() => {
    if (stagingCreatorBypass) {
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    if (user?.id && selectedType === "model") {
      const pollVerificationStatus = async () => {
        try {
          const verificationData = await getDiditVerificationByUserIdAPI(
            user.id
          );

          if (verificationData?.didit_verifications?.[0]?.status) {
            const currentStatus = normalizeDiditStatus(
              verificationData.didit_verifications[0].status
            );
            sessionStorage.setItem("diditVerificationStatus", currentStatus);
            setVerificationStatus(currentStatus);

            if (lastPolledVerificationStatus.current !== currentStatus) {
              lastPolledVerificationStatus.current = currentStatus;
              if (isDiditFailureStatus(currentStatus)) {
                trackDiditVerificationFailureOutcome({
                  user_id: user.id,
                  status: currentStatus,
                  failure_source: "status_poll",
                });
              }
            }

            // Handle status-specific actions
            if (currentStatus === "approved") {
              // showToast("🎉 Your verification has been approved!", "success");
              // Clear any existing session URL since verification is complete
              clearSessionUrl();
              // setSessionUrl(undefined);
              // no-op
            } else if (currentStatus === "in_progress") {
              // Keep the iframe visible for in_progress status
              if (verificationData.didit_verifications[0].session_url) {
                setSessionUrl(
                  verificationData.didit_verifications[0].session_url
                );
                saveSessionUrl(
                  verificationData.didit_verifications[0].session_url
                );
                // no-op
              }
            } else if (currentStatus === "in_review") {
              // showToast(
              //   "🔍 Your verification is under review. Please wait for the result.",
              //   "info"
              // );
              // Clear session URL for in_review status
              clearSessionUrl();
              // setSessionUrl(undefined);
              // no-op
            } else if (
              currentStatus === "declined" ||
              currentStatus === "kyc_expired" ||
              currentStatus === "expired" ||
              currentStatus === "abandoned" ||
              currentStatus === "unknown" ||
              currentStatus === "pending"
            ) {
              // For these statuses, clear session data and allow restart
              // showToast(
              //   `❌ Your verification was ${currentStatus}. You can start a new verification.`,
              //   "error"
              // );
              // Clear session URL for these statuses
              clearSessionUrl();
              // setSessionUrl(undefined);
              // no-op
            }
          } else {
            // No verification data found, set to not_started
            setVerificationStatus("not_started");
            sessionStorage.setItem("diditVerificationStatus", "not_started");
          }

          setDiditVerificationData(verificationData);
        } catch (error) {
          console.error("Error fetching Didit verification status:", error);
          // On error, set to not_started to allow retry
          setVerificationStatus("not_started");
        }
      };

      // Call immediately
      pollVerificationStatus();

      // Set up interval to poll every second
      intervalId = setInterval(pollVerificationStatus, 3400);
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedType, user?.id, clearSessionUrl, saveSessionUrl]);

  // Check Didit verification status for creators
  useEffect(() => {
    if (stagingCreatorBypass) {
      return;
    }

    // Get status from sessionStorage if available
    const savedStatus = sessionStorage.getItem("diditVerificationStatus");
    if (savedStatus) {
      setVerificationStatus(savedStatus as any);
      return;
    }

    // Set status based on verification data
    if (
      selectedType === "model" &&
      diditVerificationData?.didit_verifications?.[0]?.status
    ) {
      const currentStatus = normalizeDiditStatus(
        diditVerificationData.didit_verifications[0].status
      );
      setVerificationStatus(currentStatus);
      sessionStorage.setItem("diditVerificationStatus", currentStatus);

      // Handle session URL for in_progress status
      if (
        currentStatus === "in_progress" &&
        diditVerificationData.didit_verifications[0].session_url
      ) {
        setSessionUrl(diditVerificationData.didit_verifications[0].session_url);
        saveSessionUrl(
          diditVerificationData.didit_verifications[0].session_url
        );
        // no-op
      }
    } else if (selectedType === "model") {
      // No verification data found, set to not_started
      setVerificationStatus("not_started");
      sessionStorage.setItem("diditVerificationStatus", "not_started");
    }
  }, [diditVerificationData, selectedType, step, saveSessionUrl]);

  useEffect(() => {
    if (selectedType !== "model" || !verificationStatus) {
      return;
    }

    const normalizedStatus = normalizeDiditStatus(verificationStatus);

    if (
      normalizedStatus === "in_progress" &&
      !verificationStartedTracked.current
    ) {
      verificationStartedTracked.current = true;
      trackVerificationStarted({ user_id: user?.id });
      trackVerificationStepStarted({
        user_id: user?.id,
        step_type: "id_check",
      });
      if (getDiditVerificationAttemptCount() === 0) {
        registerDiditVerificationAttempt({
          user_id: user?.id,
          source: "auto_resume",
        });
      }
      return;
    }

    if (verificationCompletedTracked.current) {
      return;
    }

    if (isDiditSuccessStatus(normalizedStatus)) {
      verificationCompletedTracked.current = true;
      trackVerificationStepCompleted({
        user_id: user?.id,
        step_type: "id_check",
        result: "passed",
      });
      trackVerificationCompleted({
        user_id: user?.id,
        result: "approved",
        attempt_number: getDiditVerificationAttemptCount(),
      });
      return;
    }

    if (isDiditFailureStatus(normalizedStatus)) {
      verificationCompletedTracked.current = true;
      trackDiditVerificationFailureOutcome({
        user_id: user?.id,
        status: normalizedStatus,
        failure_source: "status_poll",
      });
    }
  }, [selectedType, verificationStatus, user?.id]);

  if (user?.isAdmin) {
    return <Redirect to="/" />;
  }

  const renderStepsSidebar = () => {
    const getSteps = () => {
      if (selectedType === "viewer") {
        return [
          { number: 1, label: "Choose your account type" },
          { number: 2, label: "Add your profile info" },
        ];
      } else {
        return [
          { number: 1, label: "Choose Account Type" },
          { number: 2, label: "Complete Profile" },
          { number: 3, label: "Identity Verification" },
        ];
      }
    };

    const steps = getSteps();

    const handleLogout = () => {
      resetMixpanelIdentity();
      clearTokens();
      dispatch(logoutUser());
      localStorage.removeItem("user");
      sessionStorage.clear();
      history.push("/login");
    };

    return (
      <Box
        className={creatorClasses.stepsSidebar}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box style={{ display: "flex", flexDirection: "column" }}>
            <img
              src={Logo}
              alt="Sugar Cribs logo"
              className={creatorClasses.sidebarLogo}
            />
            <Typography className={creatorClasses.sidebarText}>
              Create your account in a few clicks
            </Typography>
          </Box>
          {steps.map((stepItem, index) => {
            const isCompleted = step > stepItem.number;
            const isActive = step === stepItem.number;
            const stepNumber = String(stepItem.number).padStart(2, "0");
            const canNavigate = stepItem.number <= step;

            return (
              <Box key={stepItem.number}>
                <Box
                  style={{
                    borderRadius: "8px",
                    backgroundColor: "transparent",
                    color: isActive ? "#FFFFFF" : "#FFFFFF80",
                    fontSize: "14px",
                    fontWeight: isActive ? 600 : 400,
                    cursor: canNavigate ? "pointer" : "default",
                    transition: "all 0.3s ease",
                  }}
                  onClick={
                    canNavigate ? () => goToStep(stepItem.number) : undefined
                  }
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Box
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "9999px",
                        backgroundColor: isCompleted
                          ? "#FE5A97"
                          : isActive
                          ? "transparent"
                          : "transparent",
                        border: isActive
                          ? "1px solid #FE5A97"
                          : isCompleted
                          ? "none"
                          : "1px solid #F7B4C766",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: isActive
                          ? "#FFFFFF"
                          : isCompleted
                          ? "#000000"
                          : "#FFFFFF",
                        flexShrink: 0,
                      }}
                    >
                      {isCompleted ? (
                        <Check size={18} color="#000000" />
                      ) : (
                        stepNumber
                      )}
                    </Box>
                    <Typography
                      style={{
                        fontSize: "18px",
                        fontWeight: isActive || isCompleted ? 700 : 400,
                        color: "#FFFFFF",
                      }}
                    >
                      {stepItem.label}
                    </Typography>
                  </Box>
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    style={{
                      width: "1px",
                      minHeight: "20px",
                      backgroundColor: "#FFFFFF1A",
                      marginLeft: "14px",
                      marginTop: "8px",
                      marginBottom: "8px",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
        {/* Log out button for desktop only */}
        {!isMobile && (
          <Box
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              marginTop: "auto",
              paddingTop: "24px",
            }}
          >
            <img
              src={ArrowBackRightIcon}
              alt="Log out"
              style={{
                width: "20px",
                height: "20px",
              }}
            />
            <Typography
              style={{
                fontSize: "20px",
                fontWeight: 400,
                color: "#FE5A97",
                backgroundColor: "transparent",
              }}
            >
              Log out
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderMobileProgress = () => {
    const getSteps = () => {
      if (selectedType === "viewer") {
        return [
          { number: 1, label: "Choose your account type" },
          { number: 2, label: "Add your profile info" },
        ];
      } else {
        return [
          { number: 1, label: "Choose Account Type" },
          { number: 2, label: "Complete Profile" },
          { number: 3, label: "Identity Verification" },
        ];
      }
    };

    const steps = getSteps();

    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "24px",
          width: "100%",
        }}
      >
        {steps.map((stepItem, index) => {
          const isCompleted = step > stepItem.number;
          const isActive = step === stepItem.number;
          const stepNumber = String(stepItem.number).padStart(2, "0");
          const canNavigate = stepItem.number <= step;

          return (
            <Box
              key={stepItem.number}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: canNavigate ? "pointer" : "default",
              }}
              onClick={
                canNavigate ? () => goToStep(stepItem.number) : undefined
              }
            >
              <Box
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "9999px",
                  backgroundColor: isCompleted
                    ? "#FE5A97"
                    : isActive
                    ? "transparent"
                    : "transparent",
                  border: isActive
                    ? "1px solid #FE5A97"
                    : isCompleted
                    ? "none"
                    : "1px solid #F7B4C766",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isActive
                    ? "#FFFFFF"
                    : isCompleted
                    ? "#000000"
                    : "#FFFFFF",
                  flexShrink: 0,
                }}
              >
                {isCompleted ? <Check size={18} color="#000000" /> : stepNumber}
              </Box>
              {index < steps.length - 1 && (
                <Box
                  style={{
                    width: "24px",
                    height: "1px",
                    backgroundColor: isCompleted ? "#FE5A97" : "#F7B4C766",
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderStepContent = (content: React.ReactNode) => {
    if (!content) return null;
    if (step === 1) {
      return content;
    }
    if (isMobile) {
      // For mobile: show horizontal progress bar at the top
      return (
        <Box>
          {renderMobileProgress()}
          <Box className={creatorClasses.stepsContent}>{content}</Box>
        </Box>
      );
    }
    // For desktop: show sidebar layout
    return (
      <Box className={creatorClasses.stepsLayout}>
        {renderStepsSidebar()}
        <Box className={creatorClasses.stepsContent}>{content}</Box>
      </Box>
    );
  };

  return (
    <OnboardingLayout
      showHeader={step === 1}
      maxWidth={step === 1 ? 880 : 1195}
      hasSidebar={step > 1}
    >
      <StandardPageHelmet seo={seo} />
      <Box className={creatorClasses.container}>
        {step === 1 && (
          <>
            <Typography variant="h4" className={creatorClasses.title}>
              Choose <span>Account Type</span>
            </Typography>
            <Paper className={creatorClasses.selectionCard}>
              <Box className={creatorClasses.optionsContainer}>
                <Box
                  className={`${creatorClasses.optionCard} ${
                    selectedType === "model" ? creatorClasses.selectedCard : ""
                  }`}
                  onClick={() => setSelectedType("model")}
                >
                  <Box
                    className={`${creatorClasses.optionImageWrapper} ${
                      selectedType === "model"
                        ? creatorClasses.selectedImageWrapper
                        : ""
                    }`}
                  >
                    <img
                      src={CreatorLogo}
                      alt="Content Creator"
                      className={creatorClasses.optionImage}
                    />
                  </Box>
                  <Box className={creatorClasses.optionLabel}>
                    I want to create content
                  </Box>
                </Box>
                <Box
                  className={`${viewerClasses.optionCard} ${
                    selectedType === "viewer" ? viewerClasses.selectedCard : ""
                  }`}
                  onClick={() => setSelectedType("viewer")}
                >
                  <Box
                    className={`${viewerClasses.optionImageWrapper} ${
                      selectedType === "viewer"
                        ? viewerClasses.selectedImageWrapper
                        : ""
                    }`}
                  >
                    <img
                      src={ViewerLogo}
                      alt="Viewer"
                      className={creatorClasses.optionImage}
                    />
                  </Box>
                  <Box className={viewerClasses.optionLabel}>
                    I want to watch content
                  </Box>
                </Box>
              </Box>

              <Box className={creatorClasses.optionDivider} />

              <Box display="flex" justifyContent="flex-end" width="100%">
                <Box className={creatorClasses.buttonContainer}>
                  <Button
                    variant="contained"
                    className={creatorClasses.button}
                    disabled={!selectedType}
                    onClick={() => {
                      handleTypeChange(selectedType);
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Paper>
          </>
        )}

        {renderStepContent(
          step === 2 && selectedType === "viewer" && (
            <ViewerProfileStep
              isMobile={isMobile}
              profileData={profileData}
              viewerErrors={viewerErrors}
              handleFieldChange={handleViewerFieldChange}
              handleFieldBlur={handleViewerFieldBlur}
              isCheckingUsername={isCheckingUsername}
              usernameAvailable={usernameAvailable}
              setUploadMediaModalOpen={setUploadMediaModalOpen}
              prevStep={prevStep}
              isFormValid={isViewerFormValid()}
              handleProfileChange={handleProfileChange}
              isSubmitting={isSubmittingProfile}
            />
          )
        )}
        {renderStepContent(
          step === 2 && selectedType === "model" && (
            <CreatorProfileStep
              isMobile={isMobile}
              profileData={profileData}
              creatorErrors={creatorErrors}
              handleFieldChange={handleCreatorFieldChange}
              handleFieldBlur={handleCreatorFieldBlur}
              isCheckingUsername={isCheckingUsername}
              usernameAvailable={usernameAvailable}
              setUploadMediaModalOpen={setUploadMediaModalOpen}
              prevStep={prevStep}
              isFormValid={isCreatorFormValid()}
              handleProfileChange={handleProfileChange}
              isSubmitting={isSubmittingProfile}
            />
          )
        )}
        {renderStepContent(
          step === 3 && selectedType === "model" && (
            <CreatorVerificationStep
              isMobile={isMobile}
              diditVerificationData={diditVerificationData}
              sessionUrl={sessionUrl}
              popupBlocked={popupBlocked}
              setPopupBlocked={setPopupBlocked}
              startDiditVerification={startDiditVerification}
              handleManualPopupOpen={handleManualPopupOpen}
              prevStep={prevStep}
              nextStep={nextStep}
              agreementAccepted={agreementAccepted}
              setAgreementAccepted={setAgreementAccepted}
              imageUsageAgreed={imageUsageAgreed}
              setImageUsageAgreed={setImageUsageAgreed}
              isFinalizing={isFinalizing}
              handleCompleteOnboarding={handleCompleteOnboarding}
              stagingCreatorBypass={stagingCreatorBypass}
              getStatusMessage={getStatusMessage}
            />
          )
        )}
      </Box>

      {/* Upload Media Modal */}
      <UploadMediaModal
        open={uploadMediaModalOpen}
        onClose={() => setUploadMediaModalOpen(false)}
        onSave={file => {
          // Handle file save based on current step and user type
          if (step === 2) {
            if (selectedType === "model") {
              handleCreatorFieldChange("profileImg", file);
            } else {
              handleViewerFieldChange("profileImg", file);
            }
          }
        }}
        acceptedTypes={["SVG", "PNG", "GIF"]}
        maxDimensions="800x400px"
      />
    </OnboardingLayout>
  );
};

export default ChooseUserType;
